import type { Settings } from './schemas/settings.js'

export type LatePolicy = Pick<
  Settings,
  | 'latePolicyOpenTime'
  | 'latePolicyBufferMinutes'
  | 'latePolicyMultiplier'
  | 'latePolicyWorkingDaysPerMonth'
  | 'latePolicyShiftMinutes'
>

export interface LateDeduction {
  /** Raw minutes after opening time, before the buffer or the multiplier. */
  minutesLate: number
  /** minutesLate × multiplier — what actually gets paid for. */
  penaltyMinutes: number
  deductionMinor: number
}

function parseHHmm(value: string): number {
  const [h, m] = value.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

/**
 * What one late arrival costs, per the workshop's configured policy.
 *
 * Returns null when there is nothing to charge: the arrival is inside the
 * buffer (early arrivals give a negative minutesLate, which is <= the buffer
 * too), or the worker has no monthly salary on file to derive a rate from.
 *
 * The buffer is a cliff, not a discount — once minutesLate exceeds it, the
 * FULL lateness from opening time is what gets multiplied, not just the part
 * past the buffer. Arriving 30 minutes late is 60 penalty-minutes, not 30.
 */
export function computeLateDeduction(
  arrivalTime: string,
  monthlySalaryMinor: number | null,
  policy: LatePolicy,
): LateDeduction | null {
  const minutesLate = parseHHmm(arrivalTime) - parseHHmm(policy.latePolicyOpenTime)
  if (minutesLate <= policy.latePolicyBufferMinutes) return null
  if (!monthlySalaryMinor || monthlySalaryMinor <= 0) return null

  const penaltyMinutes = minutesLate * policy.latePolicyMultiplier
  const perMinuteRateMinor =
    monthlySalaryMinor / (policy.latePolicyWorkingDaysPerMonth * policy.latePolicyShiftMinutes)

  return {
    minutesLate,
    penaltyMinutes,
    deductionMinor: Math.round(penaltyMinutes * perMinuteRateMinor),
  }
}
